import { ErrorBoundary, Footer } from "@portal-frontend-ssr/ui";
import { useLoaderData } from "@remix-run/react";
import { Layout2Columns } from "~/shared/components/Layout2Columns";
import { Header } from "./components/Header";
import { TournamentTickets } from "./components/TournamentTickets/TournamentTickets";
import { TournamentSponsors } from "./components/TournamentSponsors";
import { TournamentPrizesGrid } from "./components/TournamentPrizesGrid";
import { Background } from "./components/Background/Background";
import { AdContainer } from "~/shared/components/AdContainer";
import { StickySideRailAds } from "~/shared/components/StickySideRailAds";
import { TournamentTimeline } from "~/components/TournamentTimeline";
import { TournamentTeamsGrid } from "./components/TournamentTeamsGrid";
import { DateTime } from "luxon";
import { useMemo } from "react";
import { TournamentTabs } from "./components/TournamentTabs/TournamentTabs";
import { BountyStandings } from "./components/BountyStandings/BountyStandings";
import { TournamentPageSchedule } from "./components/TournamentPageSchedule";
import { tournamentPageLoader } from "./loader";
import { Brackets } from "~/shared/components/Brackets";
import { tournamentPageMeta } from "./meta";

export const loader = tournamentPageLoader;
export const meta = tournamentPageMeta;

export const Component = () => {
  const {
    tournament,
    tournamentPrizes,
    gameId,
    brackets,
    tournamentTimeline,
    tournamentTeamSlots,
    circuitTournaments,
    liveBroadcasts,
  } = useLoaderData<typeof loader>();

  const isPastTournament = useMemo(() => {
    return !!tournament?.endDate && tournament.endDate < DateTime.utc().toISO();
  }, [tournament]);

  return (
    <>
      {gameId !== "rl" && <StickySideRailAds itemsWrapperClassName="!max-w-7xl" />}
      {gameId !== "rl" && <AdContainer type="horizontal_sticky" />}
      <Background gameId={gameId} />
      <Layout2Columns className="m-nav min-h-fill-available">
        <Layout2Columns.Content>
          <div className="mt-0 flex flex-col gap-3 sd:mt-8 sd:gap-5">
            <Header tournament={tournament} />
            <ErrorBoundary>
              <TournamentTabs tournament={tournament} circuitTournaments={circuitTournaments} />
            </ErrorBoundary>
            <ErrorBoundary>
              <TournamentPageSchedule
                brackets={brackets}
                liveBroadcasts={liveBroadcasts}
                tournament={tournament}
                gameId={gameId}
              />
            </ErrorBoundary>
            <ErrorBoundary>
              <div className="sd:hidden">
                <TournamentTickets tournament={tournament} />
              </div>
            </ErrorBoundary>
            <ErrorBoundary>
              <BountyStandings brackets={brackets} tournamentTeamSlots={tournamentTeamSlots} />
            </ErrorBoundary>
            <ErrorBoundary>
              <h2 className="font-style-label-l3">Brackets</h2>
              <div className="flex flex-col gap-1">
                <Brackets brackets={brackets} gameId={gameId} />
              </div>
            </ErrorBoundary>
            {!isPastTournament && (
              // Swap module placement for past tournaments
              <ErrorBoundary>
                <TournamentTeamsGrid
                  tournamentTeamSlots={tournamentTeamSlots}
                  disableLinkToTeamPage={gameId !== "cs"}
                />
              </ErrorBoundary>
            )}
            <ErrorBoundary>
              <TournamentPrizesGrid
                tournamentPrizes={tournamentPrizes}
                tournamentTotalPrizePool={tournament.prizePool}
              />
            </ErrorBoundary>
            {isPastTournament && (
              // Swap module placement for past tournaments
              <ErrorBoundary>
                <TournamentTeamsGrid
                  tournamentTeamSlots={tournamentTeamSlots}
                  disableLinkToTeamPage={gameId !== "cs"}
                />
              </ErrorBoundary>
            )}
            <ErrorBoundary>
              <TournamentSponsors tournament={tournament} />
            </ErrorBoundary>
          </div>
        </Layout2Columns.Content>
        <Layout2Columns.SideBar>
          <ErrorBoundary>
            <div className="hidden sd:block">
              <TournamentTickets tournament={tournament} />
            </div>
          </ErrorBoundary>
          {tournamentTimeline && (
            <ErrorBoundary>
              <TournamentTimeline data={tournamentTimeline} />
            </ErrorBoundary>
          )}
        </Layout2Columns.SideBar>
      </Layout2Columns>
      <Footer className="mt-5" />
    </>
  );
};

export default Component;
